export const CANONICAL_ACTIVITY_TYPES = [
  "cycling",
  "running",
  "swimming",
  "walking",
  "hiking",
  "strength",
  "yoga",
  "pilates",
  "tai_chi",
  "mind_and_body",
  "meditation",
  "breathwork",
  "stretching",
  "barre",
  "elliptical",
  "rowing",
  "cardio",
  "hiit",
  "stair_climbing",
  "step_training",
  "jump_rope",
  "fitness_gaming",
  "cross_training",
  "bootcamp",
  "circuit_training",
  "core",
  "boxing",
  "kickboxing",
  "martial_arts",
  "group_exercise",
  "skiing",
  "snowboarding",
  "snow_sports",
  "snowshoeing",
  "skating",
  "surfing",
  "kayaking",
  "sailing",
  "paddling",
  "water_fitness",
  "water_polo",
  "water_sports",
  "diving",
  "snorkeling",
  "tennis",
  "table_tennis",
  "squash",
  "racquetball",
  "badminton",
  "pickleball",
  "padel",
  "basketball",
  "soccer",
  "american_football",
  "australian_football",
  "gaelic_football",
  "rugby",
  "hockey",
  "lacrosse",
  "baseball",
  "softball",
  "volleyball",
  "cricket",
  "handball",
  "golf",
  "disc_golf",
  "climbing",
  "hangboard",
  "dance",
  "triathlon",
  "multisport",
  "disc_sports",
  "equestrian",
  "fencing",
  "fishing",
  "hunting",
  "gymnastics",
  "archery",
  "bowling",
  "curling",
  "wrestling",
  "track_and_field",
  "play",
  "navigation",
  "geocaching",
  "skydiving",
  "paragliding",
  "preparation_and_recovery",
  "transition",
  "other",
] as const;

export type CanonicalActivityType = (typeof CANONICAL_ACTIVITY_TYPES)[number];

export function isActivityDetailType(
  activityType: string,
  detailType: "strength" | "climbing" | "hangboard",
): boolean {
  return activityType === detailType;
}

export const ACTIVITY_MODALITIES = [
  "road",
  "mountain",
  "gravel",
  "indoor",
  "virtual",
  "electric",
  "cyclocross",
  "track",
  "bmx",
  "trail",
  "open_water",
  "functional",
  "mixed",
  "mixed_metabolic",
  "cross_country",
  "downhill",
  "paddleboard",
  "ice",
  "cardio",
  "social",
  "hand_cycle",
  "wheelchair",
  "cooldown",
] as const;

export type ActivityModality = (typeof ACTIVITY_MODALITIES)[number];

export interface ActivityClassification {
  canonicalType: CanonicalActivityType;
  modality: ActivityModality | null;
}

export interface ProviderActivityType extends ActivityClassification {
  providerType: string;
}

export const LEGACY_ACTIVITY_TYPES = [
  "cycling",
  "road_cycling",
  "mountain_biking",
  "gravel_cycling",
  "indoor_cycling",
  "virtual_cycling",
  "e_bike_cycling",
  "cyclocross",
  "track_cycling",
  "bmx",
  "running",
  "trail_running",
  "swimming",
  "open_water_swimming",
  "walking",
  "hiking",
  "strength",
  "strength_training",
  "functional_strength",
  "gym",
  "yoga",
  "pilates",
  "tai_chi",
  "mind_and_body",
  "meditation",
  "breathwork",
  "stretching",
  "flexibility",
  "barre",
  "elliptical",
  "rowing",
  "cardio",
  "hiit",
  "mixed_cardio",
  "mixed_metabolic_cardio",
  "stair_climbing",
  "stairmaster",
  "stairs",
  "step_training",
  "jump_rope",
  "fitness_gaming",
  "cross_training",
  "bootcamp",
  "circuit_training",
  "functional_fitness",
  "core",
  "core_training",
  "boxing",
  "kickboxing",
  "martial_arts",
  "group_exercise",
  "skiing",
  "cross_country_skiing",
  "downhill_skiing",
  "snowboarding",
  "snow_sports",
  "snowshoeing",
  "skating",
  "surfing",
  "kayaking",
  "sailing",
  "paddle_sports",
  "paddleboarding",
  "paddling",
  "water_fitness",
  "water_polo",
  "water_sports",
  "aqua_fitness",
  "underwater_diving",
  "diving",
  "snorkeling",
  "tennis",
  "table_tennis",
  "squash",
  "racquetball",
  "badminton",
  "pickleball",
  "padel",
  "paddle_racquet",
  "basketball",
  "soccer",
  "football",
  "american_football",
  "australian_football",
  "rugby",
  "hockey",
  "ice_hockey",
  "lacrosse",
  "baseball",
  "softball",
  "volleyball",
  "cricket",
  "handball",
  "golf",
  "disc_golf",
  "climbing",
  "rock_climbing",
  "dance",
  "dancing",
  "cardio_dance",
  "social_dance",
  "triathlon",
  "multisport",
  "hand_cycling",
  "wheelchair_walk",
  "wheelchair_run",
  "disc_sports",
  "equestrian",
  "fencing",
  "fishing",
  "hunting",
  "gymnastics",
  "archery",
  "bowling",
  "curling",
  "wrestling",
  "track_and_field",
  "play",
  "navigation",
  "geocaching",
  "skydiving",
  "paragliding",
  "preparation_and_recovery",
  "cooldown",
  "transition",
  "other",
] as const;

export type LegacyActivityType = (typeof LEGACY_ACTIVITY_TYPES)[number];
export type NormalizedActivityType = LegacyActivityType | CanonicalActivityType;

export const LEGACY_ACTIVITY_TYPE_CLASSIFICATIONS = {
  cycling: { canonicalType: "cycling", modality: null },
  road_cycling: { canonicalType: "cycling", modality: "road" },
  mountain_biking: { canonicalType: "cycling", modality: "mountain" },
  gravel_cycling: { canonicalType: "cycling", modality: "gravel" },
  indoor_cycling: { canonicalType: "cycling", modality: "indoor" },
  virtual_cycling: { canonicalType: "cycling", modality: "virtual" },
  e_bike_cycling: { canonicalType: "cycling", modality: "electric" },
  cyclocross: { canonicalType: "cycling", modality: "cyclocross" },
  track_cycling: { canonicalType: "cycling", modality: "track" },
  bmx: { canonicalType: "cycling", modality: "bmx" },
  running: { canonicalType: "running", modality: null },
  trail_running: { canonicalType: "running", modality: "trail" },
  swimming: { canonicalType: "swimming", modality: null },
  open_water_swimming: { canonicalType: "swimming", modality: "open_water" },
  walking: { canonicalType: "walking", modality: null },
  hiking: { canonicalType: "hiking", modality: null },
  strength: { canonicalType: "strength", modality: null },
  strength_training: { canonicalType: "strength", modality: null },
  functional_strength: { canonicalType: "strength", modality: "functional" },
  gym: { canonicalType: "strength", modality: null },
  yoga: { canonicalType: "yoga", modality: null },
  pilates: { canonicalType: "pilates", modality: null },
  tai_chi: { canonicalType: "tai_chi", modality: null },
  mind_and_body: { canonicalType: "mind_and_body", modality: null },
  meditation: { canonicalType: "meditation", modality: null },
  breathwork: { canonicalType: "breathwork", modality: null },
  stretching: { canonicalType: "stretching", modality: null },
  flexibility: { canonicalType: "stretching", modality: null },
  barre: { canonicalType: "barre", modality: null },
  elliptical: { canonicalType: "elliptical", modality: null },
  rowing: { canonicalType: "rowing", modality: null },
  cardio: { canonicalType: "cardio", modality: null },
  hiit: { canonicalType: "hiit", modality: null },
  mixed_cardio: { canonicalType: "cardio", modality: "mixed" },
  mixed_metabolic_cardio: { canonicalType: "cardio", modality: "mixed_metabolic" },
  stair_climbing: { canonicalType: "stair_climbing", modality: null },
  stairmaster: { canonicalType: "stair_climbing", modality: null },
  stairs: { canonicalType: "stair_climbing", modality: null },
  step_training: { canonicalType: "step_training", modality: null },
  jump_rope: { canonicalType: "jump_rope", modality: null },
  fitness_gaming: { canonicalType: "fitness_gaming", modality: null },
  cross_training: { canonicalType: "cross_training", modality: null },
  bootcamp: { canonicalType: "bootcamp", modality: null },
  circuit_training: { canonicalType: "circuit_training", modality: null },
  functional_fitness: { canonicalType: "strength", modality: "functional" },
  core: { canonicalType: "core", modality: null },
  core_training: { canonicalType: "core", modality: null },
  boxing: { canonicalType: "boxing", modality: null },
  kickboxing: { canonicalType: "kickboxing", modality: null },
  martial_arts: { canonicalType: "martial_arts", modality: null },
  group_exercise: { canonicalType: "group_exercise", modality: null },
  skiing: { canonicalType: "skiing", modality: null },
  cross_country_skiing: { canonicalType: "skiing", modality: "cross_country" },
  downhill_skiing: { canonicalType: "skiing", modality: "downhill" },
  snowboarding: { canonicalType: "snowboarding", modality: null },
  snow_sports: { canonicalType: "snow_sports", modality: null },
  snowshoeing: { canonicalType: "snowshoeing", modality: null },
  skating: { canonicalType: "skating", modality: null },
  surfing: { canonicalType: "surfing", modality: null },
  kayaking: { canonicalType: "kayaking", modality: null },
  sailing: { canonicalType: "sailing", modality: null },
  paddle_sports: { canonicalType: "paddling", modality: null },
  paddleboarding: { canonicalType: "paddling", modality: "paddleboard" },
  paddling: { canonicalType: "paddling", modality: null },
  water_fitness: { canonicalType: "water_fitness", modality: null },
  water_polo: { canonicalType: "water_polo", modality: null },
  water_sports: { canonicalType: "water_sports", modality: null },
  aqua_fitness: { canonicalType: "water_fitness", modality: null },
  underwater_diving: { canonicalType: "diving", modality: null },
  diving: { canonicalType: "diving", modality: null },
  snorkeling: { canonicalType: "snorkeling", modality: null },
  tennis: { canonicalType: "tennis", modality: null },
  table_tennis: { canonicalType: "table_tennis", modality: null },
  squash: { canonicalType: "squash", modality: null },
  racquetball: { canonicalType: "racquetball", modality: null },
  badminton: { canonicalType: "badminton", modality: null },
  pickleball: { canonicalType: "pickleball", modality: null },
  padel: { canonicalType: "padel", modality: null },
  paddle_racquet: { canonicalType: "pickleball", modality: null },
  basketball: { canonicalType: "basketball", modality: null },
  soccer: { canonicalType: "soccer", modality: null },
  football: { canonicalType: "american_football", modality: null },
  american_football: { canonicalType: "american_football", modality: null },
  australian_football: { canonicalType: "australian_football", modality: null },
  rugby: { canonicalType: "rugby", modality: null },
  hockey: { canonicalType: "hockey", modality: null },
  ice_hockey: { canonicalType: "hockey", modality: "ice" },
  lacrosse: { canonicalType: "lacrosse", modality: null },
  baseball: { canonicalType: "baseball", modality: null },
  softball: { canonicalType: "softball", modality: null },
  volleyball: { canonicalType: "volleyball", modality: null },
  cricket: { canonicalType: "cricket", modality: null },
  handball: { canonicalType: "handball", modality: null },
  golf: { canonicalType: "golf", modality: null },
  disc_golf: { canonicalType: "disc_golf", modality: null },
  climbing: { canonicalType: "climbing", modality: null },
  rock_climbing: { canonicalType: "climbing", modality: null },
  dance: { canonicalType: "dance", modality: null },
  dancing: { canonicalType: "dance", modality: null },
  cardio_dance: { canonicalType: "dance", modality: "cardio" },
  social_dance: { canonicalType: "dance", modality: "social" },
  triathlon: { canonicalType: "triathlon", modality: null },
  multisport: { canonicalType: "multisport", modality: null },
  hand_cycling: { canonicalType: "cycling", modality: "hand_cycle" },
  wheelchair_walk: { canonicalType: "walking", modality: "wheelchair" },
  wheelchair_run: { canonicalType: "running", modality: "wheelchair" },
  disc_sports: { canonicalType: "disc_sports", modality: null },
  equestrian: { canonicalType: "equestrian", modality: null },
  fencing: { canonicalType: "fencing", modality: null },
  fishing: { canonicalType: "fishing", modality: null },
  hunting: { canonicalType: "hunting", modality: null },
  gymnastics: { canonicalType: "gymnastics", modality: null },
  archery: { canonicalType: "archery", modality: null },
  bowling: { canonicalType: "bowling", modality: null },
  curling: { canonicalType: "curling", modality: null },
  wrestling: { canonicalType: "wrestling", modality: null },
  track_and_field: { canonicalType: "track_and_field", modality: null },
  play: { canonicalType: "play", modality: null },
  navigation: { canonicalType: "navigation", modality: null },
  geocaching: { canonicalType: "geocaching", modality: null },
  skydiving: { canonicalType: "skydiving", modality: null },
  paragliding: { canonicalType: "paragliding", modality: null },
  preparation_and_recovery: { canonicalType: "preparation_and_recovery", modality: null },
  cooldown: { canonicalType: "preparation_and_recovery", modality: "cooldown" },
  transition: { canonicalType: "transition", modality: null },
  other: { canonicalType: "other", modality: null },
} as const satisfies Record<LegacyActivityType, ActivityClassification>;

const LEGACY_ACTIVITY_TYPE_SET = new Set<string>(LEGACY_ACTIVITY_TYPES);

function isLegacyActivityType(activityType: string): activityType is LegacyActivityType {
  return LEGACY_ACTIVITY_TYPE_SET.has(activityType);
}

export function classifyLegacyActivityType(legacyType: LegacyActivityType): ActivityClassification {
  return LEGACY_ACTIVITY_TYPE_CLASSIFICATIONS[legacyType];
}

export function resolveProviderActivityType(
  providerType: string | number,
  normalizedType: NormalizedActivityType = "other",
): ProviderActivityType {
  const rawProviderType = String(providerType);
  if (rawProviderType.trim().length === 0) {
    throw new Error("Provider activity type must be non-empty");
  }

  const classification = isLegacyActivityType(normalizedType)
    ? classifyLegacyActivityType(normalizedType)
    : { canonicalType: normalizedType, modality: null };

  return {
    ...classification,
    providerType: rawProviderType,
  };
}

export function resolveRawProviderActivityType(
  providerType: string | number,
): ProviderActivityType {
  const rawProviderType = String(providerType);
  const normalizedType = isLegacyActivityType(rawProviderType) ? rawProviderType : "other";
  return resolveProviderActivityType(rawProviderType, normalizedType);
}
